import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, User, Phone, Mail, Smartphone, ArrowRight, ChevronDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { normalizeNameToId } from '@/lib/normalize-name';
import { useFacebookPixel } from '@/hooks/useFacebookPixel';
import villatechTextLogo from '@/assets/membresia-logos/villatech-text-logo.png';

// Country codes for the selector - comprehensive list
const countryCodes = [
  { code: '506', country: 'Costa Rica', flag: '🇨🇷' },
  { code: '1', country: 'USA/Canadá', flag: '🇺🇸' },
  { code: '52', country: 'México', flag: '🇲🇽' },
  { code: '57', country: 'Colombia', flag: '🇨🇴' },
  { code: '503', country: 'El Salvador', flag: '🇸🇻' },
  { code: '502', country: 'Guatemala', flag: '🇬🇹' },
  { code: '507', country: 'Panamá', flag: '🇵🇦' },
  { code: '505', country: 'Nicaragua', flag: '🇳🇮' },
  { code: '504', country: 'Honduras', flag: '🇭🇳' },
  { code: '51', country: 'Perú', flag: '🇵🇪' },
  { code: '56', country: 'Chile', flag: '🇨🇱' },
  { code: '54', country: 'Argentina', flag: '🇦🇷' },
  { code: '593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '591', country: 'Bolivia', flag: '🇧🇴' },
  { code: '595', country: 'Paraguay', flag: '🇵🇾' },
  { code: '598', country: 'Uruguay', flag: '🇺🇾' },
  { code: '55', country: 'Brasil', flag: '🇧🇷' },
  { code: '34', country: 'España', flag: '🇪🇸' },
  { code: '509', country: 'Haití', flag: '🇭🇹' },
  { code: '1809', country: 'Rep. Dominicana', flag: '🇩🇴' },
  { code: '53', country: 'Cuba', flag: '🇨🇺' },
  { code: '1787', country: 'Puerto Rico', flag: '🇵🇷' },
];

interface Plan {
  id: string;
  name: string;
  subtitle: string;
  regularPrice: string | number;
  launchPrice: string | number;
  annualPrice?: number;
  logo: string;
  cardLogo?: string;
  vip?: boolean;
}

interface FormData {
  nombre: string;
  telefono: string;
  email: string;
  modeloCelular: string;
}

interface MembershipCardFormProps {
  plan: Plan;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData, plan: Plan) => void;
  billingPeriod?: 'mensual' | 'trimestral' | 'anual';
}

const gradients: { [key: string]: string } = {
  bronce: 'from-amber-700 via-amber-600 to-amber-800',
  plata: 'from-slate-400 via-slate-300 to-slate-500',
  oro: 'from-amber-400 via-yellow-400 to-amber-500',
};

const textColors: { [key: string]: string } = {
  bronce: 'text-amber-950',
  plata: 'text-slate-800',
  oro: 'text-amber-950',
};

// Removed username field - now only 4 fields
const fields = [
  { key: 'nombre', label: 'Nombre y Primer Apellido', placeholder: 'Ej: Juan Pérez', icon: User },
  { key: 'telefono', label: 'Número de WhatsApp', placeholder: 'Ej: 88887777', icon: Phone },
  { key: 'email', label: 'Correo electrónico', placeholder: 'Ej: juan@email.com', icon: Mail },
  { key: 'modeloCelular', label: 'Modelo de tu celular', placeholder: 'Ej: iPhone 14 Pro', icon: Smartphone },
] as const;

export const MembershipCardForm: React.FC<MembershipCardFormProps> = ({
  plan,
  isOpen,
  onClose,
  onSubmit,
  billingPeriod = 'mensual',
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { trackLead } = useFacebookPixel();
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    telefono: '',
    email: '',
    modeloCelular: '',
  });
  const [isComplete, setIsComplete] = useState(false);
  const [countryCode, setCountryCode] = useState('506'); // Default Costa Rica
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, currentFieldIndex]);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when closed
      setCurrentFieldIndex(0);
      setFormData({
        nombre: '',
        telefono: '',
        email: '',
        modeloCelular: '',
      });
      setIsComplete(false);
      setCountryCode('506');
      setShowCountryDropdown(false);
    }
  }, [isOpen]);

  const handleNextField = () => {
    const currentField = fields[currentFieldIndex];
    const value = formData[currentField.key];

    if (!value.trim()) {
      toast({
        title: "Campo requerido",
        description: `Por favor ingresa ${currentField.label.toLowerCase()}`,
        variant: "destructive",
      });
      return;
    }

    // Validate full name (minimum 2 words)
    if (currentField.key === 'nombre') {
      const words = value.trim().split(/\s+/).filter(w => w.length > 0);
      if (words.length < 2) {
        toast({
          title: "Nombre incompleto",
          description: "Por favor ingresa tu nombre y apellido (ej: Roberto Carrillo)",
          variant: "destructive",
        });
        return;
      }
    }

    // Validate email
    if (currentField.key === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast({
        title: "Email inválido",
        description: "Por favor ingresa un correo electrónico válido",
        variant: "destructive",
      });
      return;
    }

    // Validate phone (minimum 8 digits for the local number)
    if (currentField.key === 'telefono') {
      const digits = value.replace(/\D/g, '');
      if (digits.length < 8) {
        toast({
          title: "Teléfono inválido",
          description: "Ingresa al menos 8 dígitos",
          variant: "destructive",
        });
        return;
      }
      // Update telefono with full number including country code
      setFormData(prev => ({
        ...prev,
        telefono: countryCode + digits,
      }));
    }

    if (currentFieldIndex < fields.length - 1) {
      setCurrentFieldIndex(prev => prev + 1);
    } else {
      setIsComplete(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNextField();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentField = fields[currentFieldIndex];
    const value = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      [currentField.key]: value.slice(0, 100), // Limit input length
    }));
  };

  const handleContinueToPin = () => {
    // Track Lead event - user completed form data
    const planPrice = typeof plan.launchPrice === 'number' ? plan.launchPrice : 0;
    trackLead({
      content_name: `Membresía ${plan.name}`,
      value: planPrice,
      currency: 'CRC',
    });

    // Navigate to registration with normalized name as internal identifier
    const normalizedId = normalizeNameToId(formData.nombre);
    onClose();
    navigate('/registro-membresia', {
      state: {
        registrationData: {
          nombre: formData.nombre,
          telefono: formData.telefono,
          email: formData.email,
          modeloCelular: formData.modeloCelular,
          normalizedId, // Generated from nombre
          plan: plan.id as 'bronce' | 'plata' | 'oro',
          billingPeriod: billingPeriod,
        }
      }
    });
  };

  const getCardName = () => {
    return formData.nombre ? formData.nombre.toUpperCase().slice(0, 25) : 'TU NOMBRE AQUÍ';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-zinc-900 border-zinc-700 p-0 overflow-visible max-h-[90vh] overflow-y-auto">
        {/* Card Preview - Unified design for all plans */}
        <motion.div
          className={`relative h-[200px] p-6 overflow-hidden bg-gradient-to-br ${gradients[plan.id]} shadow-[inset_0_2px_0_rgba(255,255,255,0.3)]`}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Logos - Top left (Plan logo + VillaTech text together to avoid X button) */}
          <div className="absolute top-6 left-6 flex items-center gap-2">
            <img 
              src={plan.cardLogo || plan.logo} 
              alt={plan.name}
              className="w-8 h-8 object-contain"
            />
            <img 
              src={villatechTextLogo} 
              alt="VillaTech"
              className="h-5 object-contain"
            />
          </div>

          {/* Cardholder name - updates with nombre */}
          <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
            <div>
              <div className={`text-xs uppercase tracking-wider ${textColors[plan.id]} opacity-70`}>Miembro</div>
              <motion.div 
                key={formData.nombre}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className={`text-base font-bold ${textColors[plan.id]} truncate max-w-[180px]`}
              >
                {getCardName()}
              </motion.div>
            </div>
            <div className="text-right">
              <div className={`text-xs ${textColors[plan.id]} opacity-70`}>{plan.name}</div>
              <div className={`text-lg font-bold ${textColors[plan.id]}`}>
                {billingPeriod === 'anual' && plan.annualPrice
                  ? `₡${plan.annualPrice.toLocaleString('es-CR')}/año`
                  : typeof plan.launchPrice === 'number' 
                    ? `₡${plan.launchPrice.toLocaleString('es-CR')}/mes`
                    : plan.launchPrice
                }
              </div>
            </div>
          </div>

          {/* Luxury pattern overlay */}
          <div className="absolute inset-0 opacity-[0.08]">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id={`modal-luxury-pattern-${plan.id}`} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M20 0 L40 20 L20 40 L0 20 Z" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
                  <path d="M20 8 L32 20 L20 32 L8 20 Z" fill="none" stroke="currentColor" strokeWidth="0.3" opacity="0.4" />
                  <circle cx="20" cy="0" r="1.5" fill="currentColor" opacity="0.5" />
                  <circle cx="40" cy="20" r="1.5" fill="currentColor" opacity="0.5" />
                  <circle cx="20" cy="40" r="1.5" fill="currentColor" opacity="0.5" />
                  <circle cx="0" cy="20" r="1.5" fill="currentColor" opacity="0.5" />
                  <circle cx="20" cy="20" r="1" fill="currentColor" opacity="0.3" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill={`url(#modal-luxury-pattern-${plan.id})`} />
            </svg>
          </div>
          
          {/* Subtle gradient overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/10 pointer-events-none" />
        </motion.div>

        {/* Form Section */}
        <div className="p-6">
          {!isComplete ? (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentFieldIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (currentFieldIndex === 0) {
                        onClose();
                        return;
                      }
                      setCurrentFieldIndex((prev) => Math.max(0, prev - 1));
                    }}
                    className="-ml-2"
                  >
                    ← Atrás
                  </Button>
                </div>

                {/* Progress indicator */}
                <div className="flex gap-1 mb-6">
                  {fields.map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                        idx < currentFieldIndex 
                          ? 'bg-blue-500' 
                          : idx === currentFieldIndex 
                            ? 'bg-blue-400' 
                            : 'bg-zinc-700'
                      }`}
                    />
                  ))}
                </div>
                <div className="mb-2 flex items-center gap-2 text-zinc-400">
                  {React.createElement(fields[currentFieldIndex].icon, { className: 'w-5 h-5' })}
                  <span className="text-sm">{fields[currentFieldIndex].label}</span>
                </div>

                {/* Phone field with country code selector */}
                {fields[currentFieldIndex].key === 'telefono' ? (
                  <div className="relative flex gap-2">
                    {/* Country code selector */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                        className="flex items-center gap-1 bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-4 text-white hover:bg-zinc-700 transition-colors min-w-[90px]"
                      >
                        <span className="text-lg">
                          {countryCodes.find(c => c.code === countryCode)?.flag}
                        </span>
                        <span className="text-sm">+{countryCode}</span>
                        <ChevronDown className="w-4 h-4 text-zinc-400" />
                      </button>
                      
                      {showCountryDropdown && (
                        <div className="absolute bottom-full left-0 mb-1 bg-zinc-900 border border-zinc-600 rounded-lg shadow-2xl z-[200] max-h-64 overflow-y-auto w-56">
                          {countryCodes.map((country) => (
                            <button
                              key={country.code}
                              type="button"
                              onClick={() => {
                                setCountryCode(country.code);
                                setShowCountryDropdown(false);
                                inputRef.current?.focus();
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-zinc-700 transition-colors ${
                                countryCode === country.code ? 'bg-zinc-700' : ''
                              }`}
                            >
                              <span className="text-lg">{country.flag}</span>
                              <span className="text-white text-sm font-medium">+{country.code}</span>
                              <span className="text-zinc-400 text-xs truncate">{country.country}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Phone input */}
                    <div className="relative flex-1">
                      <Input
                        ref={inputRef}
                        type="tel"
                        value={formData.telefono}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="88887777"
                        className="bg-zinc-800 border-zinc-600 text-white text-lg py-6 pr-16 
                          focus:border-blue-500 focus:ring-blue-500/20"
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        onClick={handleNextField}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 transition-colors px-3 py-1.5 rounded-lg"
                      >
                        Seguir →
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      ref={inputRef}
                      type={fields[currentFieldIndex].key === 'email' ? 'email' : 'text'}
                      value={formData[fields[currentFieldIndex].key]}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      placeholder={fields[currentFieldIndex].placeholder}
                      className="bg-zinc-800 border-zinc-600 text-white text-lg py-6 pr-16 
                        focus:border-blue-500 focus:ring-blue-500/20"
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={handleNextField}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 transition-colors px-3 py-1.5 rounded-lg"
                    >
                      Seguir →
                    </button>
                  </div>
                )}

                <p className="mt-3 text-xs text-zinc-500">
                  Paso {currentFieldIndex + 1} de {fields.length} — Presiona Enter para continuar
                </p>
              </motion.div>
            </AnimatePresence>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="w-12 h-12 bg-green-500/20 border border-green-500/40 rounded-full flex items-center justify-center mx-auto mb-3"
              >
                <Check className="w-6 h-6 text-green-400" />
              </motion.div>
              
              <h3 className="text-lg font-bold text-white mb-1">
                ¡Listo, {formData.nombre.split(' ')[0]}!
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                Creá tu PIN de 6 dígitos
              </p>

              {/* Compact summary - 2 rows only */}
              <div className="bg-zinc-800/50 rounded-xl p-3 mb-4 text-left text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400">
                    {plan.name} {billingPeriod === 'anual' ? 'Anual' : 'Mensual'}
                  </span>
                  <span className="text-white font-bold">
                    {billingPeriod === 'anual' && plan.annualPrice
                      ? `₡${plan.annualPrice.toLocaleString('es-CR')}`
                      : typeof plan.launchPrice === 'number' 
                        ? `₡${plan.launchPrice.toLocaleString('es-CR')}`
                        : plan.launchPrice
                    }
                  </span>
                </div>
                {billingPeriod === 'anual' && (
                  <p className="text-green-400 text-xs mt-1">✓ 2 meses gratis incluidos</p>
                )}
              </div>

              <Button
                onClick={handleContinueToPin}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-5 text-base"
              >
                CREAR MI PIN
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
